/**
 * RegistrationRequestPage Component
 * 라이더 회원가입 요청 페이지 (라이더 ID + 이메일 입력 + 검증 코드 확인)
 * VROONG 브랜드 통일 디자인
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegistrationRequest, useRegistrationVerify } from '../hooks/useRegistration';
import { getCookie } from '../../../core/utils/cookie';
import styles from '../styles/RegistrationPage.module.css';

const RegistrationRequestPage: React.FC = () => {
    const navigate = useNavigate();
    const [riderId, setRiderId] = useState('');
    const [email, setEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const { handleRequestEmail, isLoading, error, success, alreadyRegistered, setError, setSuccess, setAlreadyRegistered } = useRegistrationRequest();
    const { handleVerifyToken, isLoading: isVerifying, error: verifyError, riderInfo, setError: setVerifyError } = useRegistrationVerify();

    // 로그인 상태 확인
    useEffect(() => {
        const accessToken = getCookie('accessToken');
        if (accessToken) {
            setIsLoggedIn(true);
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        if (!riderId.trim()) {
            setError('라이더 ID를 입력해주세요');
            return;
        }

        if (!email.trim()) {
            setError('이메일을 입력해주세요');
            return;
        }

        // 이메일 형식 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('유효한 이메일 형식이 아닙니다');
            return;
        }

        await handleRequestEmail(riderId.trim(), email.trim());
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setVerifyError(null);

        if (!verificationCode.trim()) {
            setVerifyError('인증 코드를 입력해주세요');
            return;
        }

        if (verificationCode.length !== 6) {
            setVerifyError('인증 코드는 6자리 숫자입니다');
            return;
        }

        await handleVerifyToken(verificationCode.trim());
    };

    // 검증 성공 시 회원가입 페이지로 이동
    useEffect(() => {
        if (riderInfo && riderInfo.success) {
            navigate(`/register?token=${verificationCode}&email=${encodeURIComponent(email)}`);
        }
    }, [riderInfo, navigate, verificationCode, email]);

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const handleGoToHome = () => {
        navigate('/home');
    };

    const handleResendEmail = () => {
        setSuccess(false);
        setVerificationCode('');
    };

    // 이미 로그인된 사용자
    if (isLoggedIn) {
        return (
            <div className={styles['registration-screen']}>
                <div className={styles['registration-header']}>
                    <div className={styles['header-content']}>
                        <h1 className={styles['header-title']}>회원가입</h1>
                    </div>
                </div>
                <div className={styles['registration-container']}>
                    <div className={styles['card']}>
                        <div className={styles['status-icon-wrapper']} style={{ backgroundColor: '#3b82f6' }}>
                            <i className="fas fa-user-check" style={{ color: 'white', fontSize: '32px' }}></i>
                        </div>
                        <h2 className={styles['status-title']} style={{ color: '#3b82f6' }}>이미 로그인 되어있습니다</h2>
                        <p className={styles['status-description']}>
                            현재 로그인된 상태입니다.<br />
                            새로운 계정을 만들려면 먼저 로그아웃해주세요.
                        </p>
                        <button
                            type="button"
                            className={styles['btn-primary']}
                            onClick={handleGoToHome}
                        >
                            홈으로 이동
                        </button>
                        <button
                            type="button"
                            className={styles['btn-secondary']}
                            onClick={handleBackToLogin}
                        >
                            로그인 페이지로
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 이미 회원가입된 사용자
    if (alreadyRegistered) {
        return (
            <div className={styles['registration-screen']}>
                <div className={styles['registration-header']}>
                    <div className={styles['header-content']}>
                        <h1 className={styles['header-title']}>회원가입</h1>
                    </div>
                </div>
                <div className={styles['registration-container']}>
                    <div className={styles['card']}>
                        <div className={styles['status-icon-wrapper']} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <i className="fas fa-user-check" style={{ color: 'white', fontSize: '32px' }}></i>
                        </div>
                        <h2 className={styles['status-title']} style={{ color: '#d97706' }}>이미 회원가입된 계정입니다</h2>
                        <p className={styles['status-description']}>
                            입력하신 라이더 ID는 이미 회원가입된 계정입니다.<br />
                            로그인 페이지에서 로그인해주세요.
                        </p>
                        <button
                            type="button"
                            className={styles['btn-primary']}
                            onClick={handleBackToLogin}
                        >
                            로그인 페이지로 이동
                        </button>
                        <button
                            type="button"
                            className={styles['btn-secondary']}
                            onClick={() => setAlreadyRegistered(false)}
                        >
                            다시 시도
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles['registration-screen']}>
            {/* 헤더 */}
            <div className={styles['registration-header']}>
                <div className={styles['header-content']}>
                    <button className={styles['back-btn']} onClick={handleBackToLogin}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 className={styles['header-title']}>회원가입</h1>
                    <div className={styles['header-spacer']}></div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['registration-container']}>
                {success ? (
                    // 이메일 발송 완료 - 인증 코드 입력 화면
                    <div className={styles['card']}>
                        <div className={styles['status-icon-wrapper']}>
                            <i className="fas fa-envelope-open" style={{ color: 'white', fontSize: '32px' }}></i>
                        </div>
                        <h2 className={styles['status-title']}>이메일 발송 완료</h2>
                        <p className={styles['status-description']}>
                            <strong>{email}</strong>으로<br />
                            인증 코드가 발송되었습니다.
                        </p>
                        <span className={styles['helper-text']}>코드는 1시간 동안 유효합니다</span>

                        <form onSubmit={handleVerifyCode} className={styles['form-section']}>
                            <div className={styles['form-field']}>
                                <label className={styles['field-label']}>
                                    인증 코드 <span className={styles['required']}>*</span>
                                </label>
                                <div className={styles['input-wrapper']}>
                                    <i className={`fas fa-key ${styles['input-icon']}`}></i>
                                    <input
                                        type="text"
                                        className={`${styles['input-field']} ${styles['code-input']}`}
                                        placeholder="000000"
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                                        required
                                        disabled={isVerifying}
                                        maxLength={6}
                                    />
                                </div>
                            </div>

                            {verifyError && (
                                <div className={styles['error-message']}>
                                    <i className="fas fa-exclamation-circle"></i>
                                    {verifyError}
                                </div>
                            )}

                            <button
                                type="submit"
                                className={styles['btn-primary']}
                                disabled={isVerifying || verificationCode.length !== 6}
                            >
                                {isVerifying ? (
                                    <>
                                        <span className={styles['btn-spinner']}></span>
                                        확인 중...
                                    </>
                                ) : '인증 코드 확인'}
                            </button>

                            <button
                                type="button"
                                className={styles['link-btn']}
                                onClick={handleResendEmail}
                            >
                                인증 코드 다시 받기
                            </button>

                            <button
                                type="button"
                                className={styles['btn-secondary']}
                                onClick={handleBackToLogin}
                            >
                                로그인 페이지로 돌아가기
                            </button>
                        </form>
                    </div>
                ) : (
                    // 라이더 ID + 이메일 입력 화면
                    <div className={styles['card']}>
                        <div className={styles['card-header']}>
                            <div className={styles['card-icon']}>
                                <i className="fas fa-user-plus"></i>
                            </div>
                            <h2 className={styles['card-title']}>라이더 회원가입</h2>
                            <p className={styles['card-description']}>
                                라이더 ID와 이메일을 입력해주세요.<br />
                                입력하신 이메일로 인증 코드가 발송됩니다.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className={styles['form-section']}>
                            <div className={styles['form-field']}>
                                <label className={styles['field-label']}>
                                    라이더 ID <span className={styles['required']}>*</span>
                                </label>
                                <div className={styles['input-wrapper']}>
                                    <i className={`fas fa-id-card ${styles['input-icon']}`}></i>
                                    <input
                                        type="text"
                                        className={styles['input-field']}
                                        placeholder="라이더 ID를 입력해주세요"
                                        value={riderId}
                                        onChange={(e) => setRiderId(e.target.value.replace(/\s/g, ''))}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                                <span className={styles['helper-text']}>배민에서 발급받은 라이더 ID를 입력해주세요</span>
                            </div>

                            <div className={styles['form-field']}>
                                <label className={styles['field-label']}>
                                    이메일 <span className={styles['required']}>*</span>
                                </label>
                                <div className={styles['input-wrapper']}>
                                    <i className={`fas fa-envelope ${styles['input-icon']}`}></i>
                                    <input
                                        type="email"
                                        className={styles['input-field']}
                                        placeholder="이메일을 입력해주세요"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                                <span className={styles['helper-text']}>인증 코드를 받을 이메일 주소</span>
                            </div>

                            {error && (
                                <div className={styles['error-message']}>
                                    <i className="fas fa-exclamation-circle"></i>
                                    {error}
                                </div>
                            )}

                            <button type="submit" className={styles['btn-primary']} disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <span className={styles['btn-spinner']}></span>
                                        처리 중...
                                    </>
                                ) : '인증 코드 발송'}
                            </button>

                            <div className={styles['login-link']}>
                                <span>이미 계정이 있으신가요?</span>
                                <button type="button" onClick={handleBackToLogin} className={styles['text-link']}>
                                    로그인
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegistrationRequestPage;
